import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { LegalDisclaimer } from "../components/LegalDisclaimer";
import { Metric } from "../components/Metric";
import { formatDate, formatMoney } from "../lib/format";
import {
  fetchLegalRegistry,
  obligationHref,
  type LegalRegistry,
  type SanctionRow,
  type SummaryTotals,
} from "../lib/summary";

/**
 * Раздел «Претензии и риски» (E9, ТЗ 4.2.5 / сценарии S5–S7). Виден юристу и админу
 * (nav/navigation.ts). Четыре блока:
 *   • «Наши требования» — санкции к контрагентам по их просрочкам;
 *   • «Наши риски» — ЗЕРКАЛЬНЫЙ КОНТУР: сколько контрагент может предъявить нам (экспозиция, S6);
 *   • «Претензии без ответа» — где истёк срок ответа и возможно обращение в суд;
 *   • «Исковая давность» — требования, где до истечения осталось менее шести месяцев.
 *
 * Всё приходит ОДНИМ запросом (Р-41). Раньше страница обходила договоры по одному и складывала
 * деньги в браузере своей копией арифметики: на проектном объёме это 500 последовательных
 * обращений и второй путь расчёта, который рано или поздно разошёлся бы со сводкой директора.
 * Теперь суммы считает сервер — теми же функциями, что карточку договора и сводку.
 *
 * Развитые фильтры и сортировка — в разделе «Обязательства» (реестр E12); здесь рабочие списки
 * юриста по действующим договорам.
 */

const cellCls = "px-2 py-1.5 align-top";
const headCls = "px-2 py-1.5 text-left text-[12px] font-bold text-muted";

export function LegalPage() {
  const [registry, setRegistry] = useState<LegalRegistry | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    const res = await fetchLegalRegistry();
    if (!res.ok) {
      setError(res.message);
      return;
    }
    setRegistry(res.value);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error !== null) {
    return (
      <Shell>
        <p role="alert" className="mt-3 text-[13px] text-overdue-fg">
          {error}
        </p>
        <button
          type="button"
          className="mt-3 rounded-[var(--radius-m)] border border-border px-3 py-1.5 text-[13px] font-bold text-ink hover:bg-bg"
          onClick={() => void load()}
        >
          Повторить
        </button>
      </Shell>
    );
  }
  if (registry === null) {
    return (
      <Shell>
        <p role="status" className="mt-3 text-[13px] text-muted">
          Загрузка…
        </p>
      </Shell>
    );
  }

  const theirs = registry.sanctions.filter((r) => r.debtor === "they");
  const ours = registry.sanctions.filter((r) => r.debtor === "we");

  return (
    <Shell>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <Totals
          title="Наши требования к контрагентам"
          hint="Начисленные санкции по просрочкам контрагентов на сегодня."
          totals={registry.claimsToThem}
        />
        <Totals
          title="Наши риски (экспозиция)"
          hint="Сколько контрагенты потенциально могут предъявить нам по нашим просрочкам."
          totals={registry.exposure}
          risk
        />
      </div>

      <Block
        title="Наши требования"
        empty="Просрочек контрагентов с начисленными санкциями нет."
        rows={theirs}
      />
      <Block
        title="Наши просрочки — потенциальные требования контрагента"
        empty="Собственных просрочек с начисленными санкциями нет."
        rows={ours}
      />

      <section className="mt-6" aria-labelledby="legal-claims">
        <h2 id="legal-claims" className="text-[16px] font-bold text-ink">
          Претензии: досудебный порядок соблюдён
        </h2>
        {registry.claimsToCourt.length === 0 ? (
          <p className="mt-1 text-[13px] text-muted">
            Претензий, по которым возможно обращение в суд, нет.
          </p>
        ) : (
          <ul className="mt-2 space-y-1 text-[13px]">
            {registry.claimsToCourt.map((c) => (
              <li key={c.claimId}>
                <Link className="text-accent hover:underline" to={obligationHref(c)}>
                  {c.contractNumber}
                </Link>{" "}
                — претензия № {c.outNumber} от {formatDate(c.sentDate)}:{" "}
                {/* Путь в суд открывает и истёкший срок, и ответ по существу до его истечения. */}
                {c.expired
                  ? `срок ответа истёк ${formatDate(c.answerDue)}`
                  : `получен ответ (${c.result === "rejected" ? "отклонена" : "удовлетворена частично"})`}{" "}
                — возможно обращение в суд.
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mt-6" aria-labelledby="legal-limitation">
        <h2 id="legal-limitation" className="text-[16px] font-bold text-ink">
          Исковая давность: истекает в ближайшие полгода
        </h2>
        {registry.limitation.length === 0 ? (
          <p className="mt-1 text-[13px] text-muted">
            Требований с истекающей исковой давностью нет.
          </p>
        ) : (
          <ul className="mt-2 space-y-1 text-[13px]">
            {registry.limitation.map((r) => (
              <li key={r.obligationId}>
                <Link className="text-accent hover:underline" to={obligationHref(r)}>
                  {r.contractNumber}
                </Link>{" "}
                — {r.descr}: давность истекает {formatDate(r.limitDate)}
                {r.limitExpired ? " (срок уже истёк)" : ` (осталось ${r.limitDaysLeft ?? 0} дн.)`}
              </li>
            ))}
          </ul>
        )}
      </section>

      <LegalDisclaimer />
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <section aria-labelledby="legal-title">
      <h1 id="legal-title" className="text-[20px] font-bold text-ink">
        Претензии и риски
      </h1>
      {children}
    </section>
  );
}

function Totals({
  title,
  hint,
  totals,
  risk = false,
}: {
  title: string;
  hint: string;
  totals: SummaryTotals;
  risk?: boolean;
}) {
  return (
    <Metric
      title={title}
      value={formatMoney(totals.sanctions)}
      // «Остаток долга» — тот же термин, что в колонке таблицы ниже и в «Сводке»: один показатель
      // не должен называться на двух экранах по-разному (правило 8).
      caption={`Обязательств: ${totals.count} · остаток долга ${formatMoney(totals.debt)}`}
      hint={hint}
      risk={risk}
    />
  );
}

function Block({
  title,
  empty,
  rows,
}: {
  title: string;
  empty: string;
  rows: readonly SanctionRow[];
}) {
  const id = `legal-${title.replace(/\s+/g, "-")}`;
  return (
    <section className="mt-6" aria-labelledby={id}>
      <h2 id={id} className="text-[16px] font-bold text-ink">
        {title}
      </h2>
      {rows.length === 0 ? (
        <p className="mt-1 text-[13px] text-muted">{empty}</p>
      ) : (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full min-w-[42rem] border-collapse text-[13px]">
            <caption className="sr-only">
              {title}: договор, обязательство, срок, долг, санкции
            </caption>
            <thead>
              <tr className="border-b border-border">
                <th scope="col" className={headCls}>
                  Договор
                </th>
                <th scope="col" className={headCls}>
                  Обязательство
                </th>
                <th scope="col" className={headCls}>
                  Срок
                </th>
                <th scope="col" className={headCls}>
                  Остаток долга
                </th>
                <th scope="col" className={headCls}>
                  Санкции
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.obligationId} className="border-b border-border-soft">
                  <td className={cellCls}>
                    <Link className="text-accent hover:underline" to={obligationHref(row)}>
                      {row.contractNumber}
                    </Link>
                  </td>
                  <td className={cellCls}>
                    {row.descr}
                    {row.status === "dispute" && (
                      <span className="stamp ml-1 bg-dispute-bg text-dispute-fg">спор</span>
                    )}
                  </td>
                  <td className={cellCls}>{formatDate(row.dueUsed)}</td>
                  {/* Остаток (сумма минус полученное), а не полная сумма обязательства —
                      та же цифра, что в итогах сверху. */}
                  <td className={`${cellCls} num`}>{formatMoney(row.debt)}</td>
                  <td className={`${cellCls} num`}>
                    {formatMoney(row.sanctions)}
                    <span className="ml-1 text-[12px] text-muted">
                      {row.primary === "penalty" ? "неустойка" : "ст. 395"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
